import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { FC } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { FinanceQuotesFormValues, repaymentFrequencyOptions } from '../../templates/Checkout/Forms/CheckoutForm.types';
import styles from './FinanceQuoteRepaymentFrequencySwitch.module.scss';

interface FinanceQuoteRepaymentFrequencySwitchProps {
  fieldName: keyof FinanceQuotesFormValues;
}

export const FinanceQuoteRepaymentFrequencySwitch: FC<FinanceQuoteRepaymentFrequencySwitchProps> = ({ fieldName }) => {
  const { values, setFieldValue } = useFormikContext<FinanceQuotesFormValues>();

  const handleToggleChange = (newValue: string) => {
    const currentValue = values[fieldName];
    if (newValue !== currentValue) {
      setFieldValue(fieldName, newValue);
      pushToDataLayer({
        event: 'checkout_quote-frequency',
        value: newValue,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_FREQUENCY_SWITCH_HEADER) || 'View repayment'}
      </div>

      <fieldset className={styles.buttonContainer} role="group" aria-label="Repayment frequency switch">
        {repaymentFrequencyOptions.map(({ label, value }) => {
          const isSelected = values[fieldName] === value;
          return (
            <button
              key={value}
              type="button"
              tabIndex={0}
              aria-pressed={isSelected}
              className={clsx('c-fs-body2', 'font-semibold', {
                [styles.selectedButton]: isSelected,
                [styles.toggleButton]: !isSelected,
              })}
              onClick={() => handleToggleChange(value)}
              data-testid={`repayment-frequency`}
            >
              {label}
            </button>
          );
        })}
      </fieldset>
    </div>
  );
};
